



















































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { MiraInsurer, MiraInsurerSpellingMove } from "@/data/models/MiraInsurerAdministration";
import { moveMiraInsurerSpellings } from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  insurerErrorMessage,
  MIRA_INSURER_KEY_PATTERN,
  MiraInsurerMoveForm,
  spellingMoveRequest,
  suggestInsurerKey,
} from "@/utils/MiraInsurerAdministration";

/** What `show()` resolves with: moved, refused as changed meanwhile (the caller reloads), or closed. */
export type MiraInsurerMoveResult = "MOVED" | "RELOAD" | null;

const EMPTY_FORM: MiraInsurerMoveForm = { spellings: [], words: [], kind: "INSURER", insurerKey: null, code: "", shortName: "", name: "" };

/**
 * Schreibweisen verschieben (#2610125, part 2): moves some of an insurer's extra spellings and recognition
 * words to another insurer, to a new one created in the same step, to Kein Versicherer, or back to open,
 * so a wrong assignment is undone in one save (`moveMiraInsurerSpellingsForAdministration`).
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class MiraInsurerMoveDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  source: MiraInsurer | null = null;
  insurers: MiraInsurer[] = [];
  form: MiraInsurerMoveForm = { ...EMPTY_FORM };

  private resolve: ((result: MiraInsurerMoveResult) => void) | null = null;

  get spellingOptions(): string[] {
    return this.source?.spellings ?? [];
  }

  get wordOptions(): string[] {
    return this.source?.words ?? [];
  }

  /** The other active insurers, by short name. */
  get insurerItems(): Array<{ key: string; label: string }> {
    return this.insurers
      .filter((insurer) => !insurer.retired && insurer.key !== this.source?.key)
      .map((insurer) => ({ key: insurer.key, label: `${insurer.shortName} (${insurer.code})` }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }

  get request(): MiraInsurerSpellingMove | null {
    // Read the form first, so the computed tracks it before the early return (ui-conventions.md, Reactivity).
    const form = { ...this.form, spellings: [...this.form.spellings], words: [...this.form.words] };
    return this.source ? spellingMoveRequest(this.source, form) : null;
  }

  get codeError(): string[] {
    const code = this.form.code.trim().toUpperCase();
    return code && !MIRA_INSURER_KEY_PATTERN.test(code) ? [this.$t("admin.miraInsurers.fieldErrors.key").toString()] : [];
  }

  get effectText(): string | null {
    const source = this.source?.shortName ?? "";
    if (this.form.kind === "INSURER") {
      const target = this.insurers.find((insurer) => insurer.key === this.form.insurerKey);
      return target ? this.$t("admin.miraInsurers.move.effectInsurer", [source, target.shortName]).toString() : null;
    }
    if (this.form.kind === "NEW_INSURER") {
      return this.form.shortName.trim() ? this.$t("admin.miraInsurers.move.effectInsurer", [source, this.form.shortName.trim()]).toString() : null;
    }
    if (this.form.kind === "NOT_INSURER") return this.$t("admin.miraInsurers.move.effectNotInsurer", [source]).toString();
    return this.$t("admin.miraInsurers.move.effectRelease", [source]).toString();
  }

  /** A word ticked takes Kein Versicherer away; the choice falls back to another insurer. */
  @Watch("form.words")
  onWordsChanged(words: string[]) {
    if (words.length && this.form.kind === "NOT_INSURER") this.form.kind = "INSURER";
  }

  /** Choosing a new insurer proposes its names and key from the first ticked spelling (or word), once. */
  @Watch("form.kind")
  onKindChanged(kind: string) {
    if (kind !== "NEW_INSURER" || this.form.shortName || this.form.name) return;
    const first = this.form.spellings[0] ?? this.form.words[0] ?? "";
    this.form.shortName = first;
    this.form.name = first;
    this.form.code = suggestInsurerKey(first, this.insurers.map((insurer) => insurer.code));
  }

  /**
   * @param source - the insurer whose spellings and words move.
   * @param insurers - the whole list, for the targets and the taken keys.
   */
  show(source: MiraInsurer, insurers: MiraInsurer[]): Promise<MiraInsurerMoveResult> {
    this.source = source;
    this.insurers = insurers;
    this.form = { ...EMPTY_FORM, spellings: [], words: [] };
    this.baseDialog.show({
      title: this.$t("admin.miraInsurers.move.title", [source.shortName]).toString(),
      positiveButtonText: this.$t("admin.miraInsurers.move.action").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onMove() {
    const request = this.request;
    if (!request) return;
    this.busy = true;
    try {
      await moveMiraInsurerSpellings(request);
      SnackbarModule.showSuccess({ key: "admin.miraInsurers.move.done" });
      this.close("MOVED");
    } catch (error) {
      const message = insurerErrorMessage(error);
      SnackbarModule.showError(message);
      // The insurer changed since the list loaded: close, so the list shows what is there now.
      if (message.key.endsWith("CHANGED_MEANWHILE")) this.close("RELOAD");
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraInsurerMoveResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  finish(result: MiraInsurerMoveResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
